import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import axios from "axios";
import { getToken } from "utils/getToken";
import { formatToDate } from "utils/formatTime";
import { toast } from "react-toastify";
import {
  fieldLabel,
  focusRing,
  ghostBtn,
  input as inputCls,
  primaryBtn,
  select as selectCls,
} from "components/ui/instrument";
import "../../assets/css/scrollBar.css";

interface CreateSubscriptionFormProps {
  onClose: () => void;
}

const CreateSubscriptionForm = ({ onClose }: CreateSubscriptionFormProps) => {
  const [token, setToken] = useState(getToken());

  // State to store the form data and track success status
  const [newSubscriptionData, setNewSubscriptionData] = useState({
    mobile: "",
    plan: "PREVIEW",
    startDate: new Date(),
    endDate: new Date(new Date().getTime() + 2 * 24 * 60 * 60 * 1000), // Set end date to 2 days from today
    maxCalls: "0",
    paymentDetails: {
      amount: 0,
      sameAsMobile: false,
      paymentNumber: "",
    },
    reason: "", // Reason for the subscription
  });

  // State to track if the subscription is created successfully
  const [isSubscriptionCreated, setIsSubscriptionCreated] = useState(false);
  const [isReasonFilled, setIsReasonFilled] = useState(true);
  const [consultType, setConsultType] = useState("");
  const [doctorsData, setDoctorsData] = useState<any[]>([]);
  const [selectedDoctor, setSelectedDoctor] = useState<any>("");
  // State to track the payment amount error message
  const [paymentAmountError, setPaymentAmountError] = useState("");

  const FetchDoctorGrowthandLactation = async (category: string) => {
    if (category !== "") {
      try {
        const { data } = await axios.get(
          category === "lactation" || category === "growth"
            ? `${process.env.REACT_APP_API_URL}doctors/doctors/all/?category=${category}`
            : `${process.env.REACT_APP_API_URL}doctors/doctors/all`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Token ${getToken()}`,
            },
          }
        );
        setDoctorsData(data);
      } catch (error) {
        console.error("Error fetching doctors:", error);
        toast.error("Failed to fetch doctors");
      }
    }
  };

  useEffect(() => {
    FetchDoctorGrowthandLactation(consultType);
  }, [consultType]);

  useEffect(() => {
    setSelectedDoctor(doctorsData[0]?.id);
  }, [doctorsData]);

  // Handler for saving the new subscription
  const handleSaveNewSubscription = async () => {
    // Check if the reason field is filled
    if (!newSubscriptionData.reason) {
      setIsReasonFilled(false);
      return; // Return early if the reason field is empty
    } else {
      setIsReasonFilled(true);
    }

    try {
      // Prepare the data to be sent in the request
      const requestData = JSON.stringify({
        mobile: newSubscriptionData.mobile,
        plan_name: newSubscriptionData.plan,
        start_date: newSubscriptionData.startDate.toISOString(),
        end_date: newSubscriptionData.endDate.toISOString(),
        max_calls: newSubscriptionData.maxCalls,
        payment_number: newSubscriptionData.paymentDetails.sameAsMobile
          ? newSubscriptionData.mobile
          : newSubscriptionData.paymentDetails.paymentNumber
          ? newSubscriptionData.paymentDetails.paymentNumber
          : null,
        payment_amount: newSubscriptionData.paymentDetails.amount,
        reason: newSubscriptionData.reason,
        assigned_doctor: {
          [consultType]: parseInt(selectedDoctor),
        },
      });
      // console.log("Request Data:", requestData); // Add this line to see the request data

      // API request configuration
      const config = {
        method: "post",
        url:
          process.env.REACT_APP_API_URL +
          "subscription/support/create_subscription/",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Token ${token}`,
        },
        data: requestData,
      };

      // Make the API request
      const response = await axios(config);

      // Check the response for success and log a message
      if (response.data) {
        setIsSubscriptionCreated(true); // Set success status to show the success message
      }
    } catch (error: any) {
      if (error.response?.data?.message) {
        toast.error(error.response?.data?.message);
      } else {
        toast.error("Something went wrong!");
      }

      if (
        error.response &&
        error.response.data &&
        error.response.data.error_fields
      ) {
        const errorFields = error.response.data.error_fields;
        if (errorFields.payment_amount) {
          console.error(
            "Error creating the subscription:",
            errorFields.payment_amount
          );
          setPaymentAmountError(errorFields.payment_amount); // Set the payment amount error message
        } else {
          console.error(
            "Error creating the subscription:",
            error.response.data
          );
        }
      } else {
        console.error("Error creating the subscription:", error.message);
      }
    }
  };

  // List of available plan options
  const planOptions = [
    "COMPLEMENTARY_CHAT",
    "BASIC",
    "PRIME",
    "ESSENTIAL",
    "HOLISTIC_OLD",
    "HOLISTIC_NEW",
    "NURTURE",
    "Toilet Training Program TTP (1)",
    "BTP",
    "WTP",
    "STP",
    "OneWeek_Trial",
    "BASIC_CHAT",
    "FREE_PREVIEW",
    "YOGA_BASIC",
    "PREVIEW",
    "OLD_BASIC",
    "OLD_PRIME",
    "OLD_PREMIUM",
    "ACADEMY",
  ];

  return (
    <div>
      {/* Modal backdrop */}
      <div className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-clinic-900 opacity-75"></div>
        {/* Modal content */}
        <div className="custom-scrollbar relative z-10 mx-4 h-[95vh] w-full overflow-y-auto rounded bg-white md:mx-auto md:max-w-lg">
          <div className="container mx-auto max-w-md rounded-lg bg-white px-8 py-8 md:px-0">
            {isSubscriptionCreated ? (
              // Show success message if the subscription is created
              <div className="p-8 text-center">
                <p className="mb-4 text-lg font-semibold text-clinic-900">
                  New subscription is created for {newSubscriptionData.mobile}
                </p>
                <button
                  onClick={() => setIsSubscriptionCreated(false)}
                  className={`${primaryBtn} px-8`}
                >
                  Create Another Subscription
                </button>
              </div>
            ) : (
              // Show subscription creation form
              <div>
                <h2 className="mb-6 text-lg font-bold tracking-tight text-clinic-900">Create Subscription</h2>
                <div>
                  {/* Mobile Number */}
                  <div className="mb-2 md:mb-4">
                    <label className={fieldLabel}>
                      Mobile Number:
                    </label>
                    <input
                      type="text"
                      value={newSubscriptionData.mobile}
                      onChange={(e) =>
                        setNewSubscriptionData({
                          ...newSubscriptionData,
                          mobile: e.target.value,
                        })
                      }
                      className={`${inputCls} tabular-nums`}
                      placeholder="Enter mobile number"
                    />
                  </div>

                  {/* Plan */}
                  <div className="mb-2 md:mb-4">
                    <label className={fieldLabel}>
                      Plan:
                    </label>
                    <select
                      value={newSubscriptionData.plan}
                      onChange={(e) =>
                        setNewSubscriptionData({
                          ...newSubscriptionData,
                          plan: e.target.value,
                        })
                      }
                      className={`${selectCls} w-full`}
                    >
                      {planOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex space-x-4">
                    {/* Start Date */}
                    <div className="mb-2 md:mb-4">
                      <label className={fieldLabel}>
                        Start Date:
                      </label>
                      <DatePicker
                        selected={newSubscriptionData.startDate}
                        onChange={(date: any) =>
                          setNewSubscriptionData({
                            ...newSubscriptionData,
                            startDate: date,
                          })
                        }
                        dateFormat="dd/MM/yyyy"
                        className={`${inputCls} tabular-nums`}
                      />
                      <p className="pl-2 text-sm font-medium tabular-nums text-clinic-700">
                        {formatToDate(newSubscriptionData.startDate)}
                      </p>
                    </div>

                    {/* End Date */}
                    <div className="mb-2 md:mb-4">
                      <label className={fieldLabel}>
                        End Date:
                      </label>
                      <DatePicker
                        selected={newSubscriptionData.endDate}
                        onChange={(date: any) =>
                          setNewSubscriptionData({
                            ...newSubscriptionData,
                            endDate: date,
                          })
                        }
                        dateFormat="dd/MM/yyyy"
                        className={`${inputCls} tabular-nums`}
                      />
                      <p className="pl-2 text-sm font-medium tabular-nums text-clinic-700">
                        {formatToDate(newSubscriptionData.endDate)}
                      </p>
                    </div>
                  </div>

                  {/* Max Calls */}
                  <div className="mb-2 md:mb-4">
                    <label className={fieldLabel}>
                      Max Calls:
                      <span className="font-bold text-crit-500">*</span>
                    </label>

                    <input
                      type="text"
                      value={newSubscriptionData.maxCalls}
                      onChange={(e) =>
                        setNewSubscriptionData({
                          ...newSubscriptionData,
                          maxCalls: e.target.value,
                        })
                      }
                      className={`${inputCls} tabular-nums`}
                      placeholder="Enter max calls"
                    />
                  </div>

                  {/* Payment Details */}
                  <div className="mb-2 md:mb-4">
                    {/* Amount */}
                    <label className={fieldLabel}>
                      Payment Amount:
                      <span className="pl-1 font-bold text-clinic-500">
                        (INR(₹))
                      </span>
                    </label>
                    <div className="mb-4 flex flex-col items-center">
                      <input
                        type="number"
                        value={newSubscriptionData.paymentDetails.amount}
                        onChange={(e) =>
                          setNewSubscriptionData({
                            ...newSubscriptionData,
                            paymentDetails: {
                              ...newSubscriptionData.paymentDetails,
                              amount: e.target.valueAsNumber,
                            },
                          })
                        }
                        min="0"
                        placeholder="Enter amount"
                        className={`${inputCls} mr-2 tabular-nums`}
                      />
                      {/* Display the payment amount error message */}
                      {paymentAmountError && (
                        <p className="mt-1 text-sm text-crit-600">
                          {paymentAmountError}
                        </p>
                      )}
                    </div>
                    <label className={fieldLabel}>
                      Payment Mobile Number :
                    </label>
                    {/* Payment Number */}
                    <div className="mb-1 flex items-center ">
                      <input
                        type="text"
                        value={newSubscriptionData.paymentDetails.paymentNumber}
                        onChange={(e) =>
                          setNewSubscriptionData({
                            ...newSubscriptionData,
                            paymentDetails: {
                              ...newSubscriptionData.paymentDetails,
                              paymentNumber: e.target.value,
                            },
                          })
                        }
                        placeholder="Enter payment number"
                        className={`${inputCls} mr-2 tabular-nums`}
                      />
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={
                          newSubscriptionData.paymentDetails.sameAsMobile
                        }
                        onChange={(e) =>
                          setNewSubscriptionData({
                            ...newSubscriptionData,
                            paymentDetails: {
                              ...newSubscriptionData.paymentDetails,
                              sameAsMobile: e.target.checked,
                              paymentNumber: e.target.checked
                                ? newSubscriptionData.mobile
                                : "", // Prefill or reset payment number based on checkbox
                            },
                          })
                        }
                        className={`form-checkbox h-5 w-5 accent-action-600 ${focusRing}`}
                      />
                      <label className="ml-2 text-sm text-clinic-700">
                        Use the Same Number as Mobile Number for Payment
                      </label>
                    </div>
                    <div className="flex items-center gap-x-4">
                      <div className="mb-1 flex w-1/2 flex-col items-center">
                        <label className={fieldLabel}>
                          Consultation type
                        </label>
                        <select
                          value={consultType}
                          onChange={(e) => setConsultType(e.target.value)}
                          className={`${selectCls} mr-2 w-full`}
                        >
                          <option value="">Choose an option</option>
                          <option value="lactation">lactation</option>
                          <option value="nutrition">nutrition</option>
                          <option value="gyne">gyne</option>
                          <option value="derma">derma</option>
                          <option value="pediatric">pediatric</option>
                          <option value="growth">growth</option>
                        </select>
                      </div>
                      <div className="mb-1 flex w-1/2 flex-col items-center">
                        <label className={fieldLabel}>
                          Assigned Doctors
                        </label>
                        <select
                          value={selectedDoctor}
                          onChange={(e) => setSelectedDoctor(e.target.value)}
                          className={`${selectCls} mr-2 w-full`}
                        >
                          {doctorsData?.map((doc, i) => (
                            <option key={i} value={doc.id}>
                              {doc.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Reason */}
                  <div className="mb-2 md:mb-4">
                    <label className={fieldLabel}>
                      Reason: <span className="text-crit-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newSubscriptionData.reason}
                      onChange={(e) =>
                        setNewSubscriptionData({
                          ...newSubscriptionData,
                          reason: e.target.value,
                        })
                      }
                      className={inputCls}
                      placeholder="Enter reason for the subscription"
                    />
                    {/* Error message for empty reason */}
                    {!isReasonFilled && (
                      <p className="mt-1 text-sm text-crit-600">
                        Reason is required.
                      </p>
                    )}
                  </div>
                  {/* Save and Cancel buttons */}
                  <div className="mt-6 flex justify-end">
                    <button
                      onClick={handleSaveNewSubscription}
                      className={`${primaryBtn} mr-2 px-8`}
                    >
                      Save
                    </button>
                    <button
                      onClick={onClose} // Close the modal when Cancel is clicked
                      className={`${ghostBtn} px-8`}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Close icon */}
            <button
              type="button"
              aria-label="Close"
              className={`absolute top-0 right-0 m-3 cursor-pointer rounded p-1 text-clinic-500 transition-colors hover:text-clinic-800 motion-reduce:transition-none ${focusRing}`}
              onClick={onClose} // Close the modal when the close icon is clicked
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                aria-label="Close"
                className="icon icon-tabler icon-tabler-x"
                width={20}
                height={20}
                viewBox="0 0 24 24"
                strokeWidth="2.5"
                stroke="currentColor"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path stroke="none" d="M0 0h24v24H0z" />
                <line x1={18} y1={6} x2={6} y2={18} />
                <line x1={6} y1={6} x2={18} y2={18} />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateSubscriptionForm;
